import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared/CollaboratorAvatar';
import { type CollaboratorPublic } from '@/features/Collaborators/Data';
import { searchCollaboratorsByName, CollaboratorSearchResult } from '@/services/collaboratorSearchService';
import { collaboratorService } from '@/features/Collaborators/Services';
import { useDebounce } from '@/hooks/useDebounce';

interface CollaboratorComboboxProps {
    selectedId?: string | null;
    onChange: (collaborator: CollaboratorPublic | null) => void;
    disabled?: boolean;
    placeholder?: string;
    /**
     * Optional: Pass the full collaborator object if available to avoid initial fetch.
     * Useful when rendering many comboboxes (e.g. in a table).
     */
    initialCollaborator?: CollaboratorPublic | null;
    className?: string; // Additional classes for the input
    /** Optional: collaborator ids to hide from the search results (e.g. the current user). */
    excludeIds?: string[];
    /** Optional: id for the input element (for label association via htmlFor) */
    id?: string;
}

const CollaboratorCombobox: React.FC<CollaboratorComboboxProps> = ({
    selectedId,
    onChange,
    disabled,
    placeholder,
    initialCollaborator,
    className,
    excludeIds,
    id,
}) => {
    // Current selected item (for display)
    const [selectedItem, setSelectedItem] = useState<CollaboratorPublic | null>(initialCollaborator || null);

    // Search state
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState<CollaboratorPublic[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchError, setSearchError] = useState(false);

    // Debounce query to avoid spamming the API
    // We only trigger search if query length > 2 or if user explicitly types
    const debouncedQuery = useDebounce(query, 300);

    // Initial fetch if we have ID but no object
    useEffect(() => {
        let isMounted = true;

        const loadInitialCollaborator = async () => {
            if (selectedId && !selectedItem && !initialCollaborator) {
                // Check if we have it in options (rare case if re-selecting)
                const inOptions = options.find(o => o.id === selectedId);
                if (inOptions) {
                    setSelectedItem(inOptions);
                    return;
                }

                setIsLoading(true);
                try {
                    // We use the private service here because we don't have a public "getById" handy.
                    // Ideally handled by backend permissions, but for internal tool this is usually OK.
                    // Alternatively we could create a getPublicCollaboratorById.
                    // For now, mapping private data to the shape we need is acceptable as they share fields.
                    const data = await collaboratorService.getCollaboratorById(selectedId);
                    if (data && isMounted) {
                        // Adapt to CollaboratorPublic/SearchResult shape
                        const adapted: CollaboratorPublic = {
                            id: data.id,
                            name: data.name,
                            email: data.email,
                            status: data.status,
                            photoUrl: data.photoUrl,
                            profilePath: `/collaborators/${data.id}`, // Add best guess or existing path
                            // Add other fields if necessary
                        } as any;
                        setSelectedItem(adapted);
                    }
                } catch (error) {
                    console.error("Error loading initial collaborator", error);
                } finally {
                    if (isMounted) setIsLoading(false);
                }
            } else if (!selectedId) {
                setSelectedItem(null);
            }
        };

        // If props provided initial object, simplify sync
        if (initialCollaborator && initialCollaborator.id === selectedId) {
            setSelectedItem(initialCollaborator);
        } else {
            loadInitialCollaborator();
        }

        return () => { isMounted = false; };
    }, [selectedId, initialCollaborator]); // Leaving options out to avoid loop, simplified logic

    // Search Effect
    useEffect(() => {
        let isMounted = true;

        const doSearch = async () => {
            // Don't search if empty or very short, unless we want to show some default "recent"?
            // For now, require 1 char to start (or strict 2?)
            if (!debouncedQuery || debouncedQuery.length < 2) {
                setOptions([]);
                return;
            }

            setIsLoading(true);
            setSearchError(false);
            try {
                const results = await searchCollaboratorsByName(debouncedQuery);
                if (isMounted) {
                    // Normalize Search Results to CollaboratorPublic
                    const normalized: CollaboratorPublic[] = results.map(r => ({
                        id: r.collaboratorId,
                        name: r.name,
                        email: r.email || '',
                        status: r.status || '',
                        photoUrl: r.avatarUrl,
                        profilePath: r.profilePath,
                        positionName: r.positionName,
                        isRaroAcademyAlumnus: r.isRaroAcademyAlumnus,
                        // map other fields if needed, spread remainder or cast as any if types are loose
                    } as unknown as CollaboratorPublic));
                    setOptions(normalized);
                    setSearchError(false);
                }
            } catch (err) {
                console.error("Search failed", err);
                if (isMounted) {
                    setSearchError(true);
                    setOptions([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        doSearch();

        return () => { isMounted = false; };
    }, [debouncedQuery]);


    const handleSelectionChange = (value: CollaboratorPublic | null) => {
        setSelectedItem(value);
        onChange(value);
    };

    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const isEmailFallbackAvailable = searchError && query.length >= 2 && isValidEmail(query);

    const filteredOptions = excludeIds?.length
        ? options.filter((option) => !excludeIds.includes(option.id))
        : options;

    return (
        <Combobox
            as="div"
            value={selectedItem}
            onChange={handleSelectionChange}
            disabled={disabled}
            nullable
            by="id"
            className="mt-1"
        >
            <div className="relative">
                <Combobox.Input
                    id={id}
                    className={`input-primary w-full ${className || ''}`}
                    onChange={(event) => setQuery(event.target.value)}
                    displayValue={(item: CollaboratorPublic | null) => item?.name || ''}
                    placeholder={placeholder ?? 'Selecione um responsável'}
                    autoComplete="off"
                />
                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </Combobox.Button>

                {(filteredOptions.length > 0 || isLoading || searchError) && (
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">

                        {isLoading && (
                            <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                Buscando...
                            </div>
                        )}

                        {searchError && query.length >= 2 && (
                            <div className="relative cursor-default select-none py-2 px-4 text-amber-700 bg-amber-50">
                                <p className="font-medium">Busca indisponível</p>
                                <p className="text-sm">Digite o e-mail manualmente abaixo</p>
                            </div>
                        )}

                        {!isLoading && !searchError && filteredOptions.length === 0 && query.length >= 2 && (
                            <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                Nenhum resultado encontrado.
                            </div>
                        )}

                        {isEmailFallbackAvailable && (
                            <Combobox.Option
                                value={{ id: query, name: query, email: query } as CollaboratorPublic}
                                className="relative cursor-default select-none py-2 pl-10 pr-4 text-amber-700 bg-amber-50 hover:bg-amber-100"
                            >
                                <div className="flex items-center">
                                    <span className="block truncate">{query}</span>
                                </div>
                            </Combobox.Option>
                        )}

                        {!isLoading && filteredOptions.map((collaborator) => (
                            <Combobox.Option
                                key={collaborator.id}
                                value={collaborator}
                                className={({ active }) =>
                                    `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'}`
                                }
                            >
                                {({ active, selected }) => (
                                    <>
                                        <div className="flex items-center">
                                            <CollaboratorAvatar photoUrl={collaborator.photoUrl} name={collaborator.name} size="xsmall" />
                                            <span className={`block truncate ml-3 ${selected ? 'font-semibold' : ''}`}>
                                                {collaborator.name}
                                            </span>
                                        </div>
                                        {selected && (
                                            <span
                                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-indigo-600'}`}
                                            >
                                                <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                            </span>
                                        )}
                                    </>
                                )}
                            </Combobox.Option>
                        ))}
                    </Combobox.Options>
                )}
            </div>
        </Combobox>
    );
};

export default CollaboratorCombobox; 
